import React, { useEffect, useMemo, useRef, useState } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import DayPickerInput from 'react-day-picker/DayPickerInput';

/**
 * HEY LISTEN! Portal needs to be imported via the full path so that
 * Jest can pick up the automock!
 */
// eslint-disable-next-line no-restricted-imports
import Portal from 'next/components/ui/lib/Portal';
import {
  getEndOfToday,
  formatDate,
  parseDate,
  validateDate,
  DATE_FORMAT,
  MONTH_OPTIONS,
  YEAR_OPTIONS,
} from './lib/date-range';
import Icon from './Icon';
import Input from './Input';
import { Select } from './Select';

// Generate SVG to be used as data URIs for previous/next month icons
const PREV_ICON = renderToStaticMarkup(<Icon icon="chevron-left" />);
const NEXT_ICON = renderToStaticMarkup(<Icon icon="chevron-right" />);

/**
 * FIXME: Remove once we deprecated the existing DateInput component that
 *        imports the original styles that cause collisions.
 */
// Custom classes to prevent global style collisions
const prefix = 'DatePicker';
const classes = {
  wrapper: `${prefix}-wrapper`,
  month: `${prefix}-Month`,
  navButtonPrev: `${prefix}-NavButton ${prefix}-NavButton--prev`,
  navButtonNext: `${prefix}-NavButton ${prefix}-NavButton--next`,
  caption: `${prefix}-Caption`,
  weekdays: `${prefix}-Weekdays`,
  weekdaysRow: `${prefix}-WeekdaysRow`,
  weekday: `${prefix}-Weekday`,
  body: `${prefix}-Body`,
  week: `${prefix}-Week`,
  day: `${prefix}-Day`,
  today: 'today',
  selected: 'selected',
  disabled: 'disabled',
  outside: 'outside',
};

// Copied and adapted from react-day-picker/lib/style.css
const Wrapper = styled.div`
  /**
   * DayPickerInput
   */

  .DayPickerInput {
    display: inline-block;
    width: 100%;

    ${Input} {
      width: 100%;
    }
  }

  .DayPickerInput-OverlayWrapper {
    position: relative;
  }

  .DayPickerInput-Overlay {
    background-color: var(--white);
    border-radius: 6px;
    box-shadow: var(--level-3);
    color: var(--datepicker-color);
    left: 0;
    position: absolute;
    top: 10px;
    z-index: 1;
  }

  /**
   * DayPicker
   */

  .${prefix}-wrapper {
    user-select: none;
  }

  .${prefix}-Month {
    user-select: none;
  }

  .${prefix}-NavButton {
    background-position: center;
    background-repeat: no-repeat;
    background-size: 50%;
    cursor: pointer;
    display: inline-block;
    height: var(--regular);
    opacity: 0.6;
    position: absolute;
    right: 28px;
    top: 30px;
    width: var(--regular);

    :hover {
      opacity: 0.4;
    }
  }

  .${prefix}-NavButton--prev {
    margin-right: 20px;
    background-image: url('data:image/svg+xml;utf8, ${PREV_ICON}');
  }

  .${prefix}-NavButton--next {
    background-image: url('data:image/svg+xml;utf8, ${NEXT_ICON}');
  }

  .${prefix}-Caption {
    background-color: var(--datepicker-header-bg);
    border-top-left-radius: 6px;
    border-top-right-radius: 6px;
    padding: 16px;
  }

  .${prefix}-Weekdays {
    background-color: var(--datepicker-header-bg);
    padding: 0 16px;
  }

  .${prefix}-WeekdaysRow {
    align-items: center;
    display: flex;
    justify-content: space-around;
  }

  .${prefix}-Weekday {
    align-items: center;
    color: var(--datepicker-weekday-color);
    display: flex;
    font-size: var(--x-small);
    font-weight: var(--bold);
    justify-content: center;
    padding: 8px;

    abbr[title] {
      border-bottom: none;
      text-decoration: none;
    }
  }

  .${prefix}-Body {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    padding: 16px;
  }

  .${prefix}-Week {
    display: contents;
  }

  .${prefix}-Day {
    align-items: center;
    border: 1px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    display: flex;
    font-size: var(--regular);
    height: 40px;
    justify-content: center;
    width: 40px;
  }

  /**
   * Modifiers
   */

  .disabled {
    background-color: var(--datepicker-disabled-bg);
    border-radius: 0;
    color: var(--datepicker-disabled-color);
    cursor: default;
  }

  .outside {
    color: var(--datepicker-outside-color);
  }

  .${prefix}-Day:not(.disabled) {
    :hover {
      background-color: var(--datepicker-hover);
      color: var(--datepicker-color);
      font-weight: var(--bold);
    }
  }

  .selected:not(.disabled) {
    background-color: var(--datepicker-selected-bg);
    border: 1px solid var(--datepicker-selected-border);
    color: var(--datepicker-color);
    font-weight: var(--bold);

    :hover {
      background-color: var(--datepicker-selected-hover);
    }
  }
`;

const FooterWrapper = styled.div`
  background-color: var(--datepicker-footer-bg);
  box-shadow: 0px -4px 10px var(--tint-black);
  padding: 8px 16px;
  text-align: center;
`;

const CaptionWrapper = styled.div`
  display: flex;

  /* stylelint-disable-next-line no-descending-specificity */
  ${Select} {
    &:first-of-type {
      max-width: 120px;
      margin-right: 8px;
      min-width: 120px;
    }

    &:last-of-type {
      max-width: 80px;
      min-width: 80px;
    }
  }
`;

/**
 * Custom overlay component to support Portal rendering
 */
function Overlay({
  children,
  classNames,
  footer,
  input,
  portal,
  selectedDay: omit,
  ...props
}) {
  const { overlay, overlayWrapper } = classNames;

  if (portal && input) {
    // Absolutely position calendar to bottom left corner of the input
    const { bottom, left } = input.getBoundingClientRect();
    const position = { top: bottom + 10, left };

    return (
      <Portal>
        <Wrapper role="dialog" {...props}>
          <div className={overlay} style={position}>
            {children}
            {footer && <FooterWrapper>{footer}</FooterWrapper>}
          </div>
        </Wrapper>
      </Portal>
    );
  }

  return (
    <div role="dialog" className={overlayWrapper} {...props}>
      <div className={overlay}>
        {children}
        {footer && <FooterWrapper>{footer}</FooterWrapper>}
      </div>
    </div>
  );
}

Overlay.propTypes = {
  children: PropTypes.node,
  classNames: PropTypes.shape({
    overlay: PropTypes.string,
    overlayWrapper: PropTypes.string,
  }),
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  input: PropTypes.instanceOf(HTMLElement),
  portal: PropTypes.bool,
  selectedDay: PropTypes.instanceOf(Date),
};

/**
 * Custom caption component with month/year selects
 */
function Caption({ classNames, date, onChange, portal }) {
  const month = date.getMonth();
  const year = date.getFullYear();

  const fallback = { label: `${year}`, value: year };

  const overrides = {
    menuList: provided => ({
      ...provided,
      maxHeight: '420px',
    }),
  };

  const handleMonthChange = ({ value }) => {
    const next = new Date(date);
    onChange(new Date(next.setMonth(value)));
  };

  const handleYearChange = ({ value }) => {
    const next = new Date(date);
    onChange(new Date(next.setFullYear(value)));
  };

  return (
    <div className={classNames.caption}>
      <CaptionWrapper>
        <Select
          name="Month picker"
          onChange={handleMonthChange}
          options={MONTH_OPTIONS}
          portal={portal}
          styles={overrides}
          value={MONTH_OPTIONS.find(({ value }) => value === month)}
        />
        <Select
          name="Year picker"
          onChange={handleYearChange}
          options={YEAR_OPTIONS}
          portal={portal}
          styles={overrides}
          value={YEAR_OPTIONS.find(({ value }) => value === year) || fallback}
        />
      </CaptionWrapper>
    </div>
  );
}

Caption.propTypes = {
  classNames: PropTypes.shape({
    caption: PropTypes.string,
  }),
  date: PropTypes.instanceOf(Date),
  onChange: PropTypes.func,
  portal: PropTypes.bool,
};

/**
 * Styled date picker input component
 */
function DateInput({
  future = false,
  className,
  dayPickerProps = {},
  footer,
  inputProps: { error, ...inputProps } = {},
  onChange,
  onDayChange,
  onDayClick,
  onDayPickerHide,
  portal,
  value,
  ...props
}) {
  const $picker = useRef();

  const [{ staged, status }, setStaged] = useState({});
  const [invalid, setInvalid] = useState(false);

  // Handler to reset to initial state
  const reset = () => {
    setStaged({});
    setInvalid(false);
  };

  // Reset to initial state when value prop changes
  useEffect(reset, [value]);

  // Generate end of today
  const endOfToday = useMemo(getEndOfToday, []);

  // Handle day change and locally save date
  const handleDayChange = (date, ...rest) => {
    setStaged({ status: 'commit', staged: date });
    onDayChange?.(date, ...rest);
  };

  /**
   * NOTE: When selecting a date via click, react-day-picker returns the date at
   *       noon to "avoid timezone issues"... However, typing the date returns
   *       midnight so we normalize the date here to midnight to keep the
   *       behavior consistent.
   *       https://github.com/gpbl/react-day-picker/issues/809#issuecomment-465918888
   */
  // Handle day click, normalize date, and passthrough input instance
  const handleDayClick = (date, modifiers, ...rest) => {
    date.setHours(0);

    // If the date is not disabled, set the value so that when the calendar
    // hides, the change handler is triggered
    setStaged(modifiers.disabled ? {} : { status: 'commit', staged: date });

    onDayClick?.(date, modifiers, ...rest, $picker.current);
  };

  // Toggle calendar hide state and passthrough handler
  const handleDayPickerHide = () => {
    const $input = $picker.current.input;
    const valid = validateDate($input.value, future);
    const previous = value && new Date(value);

    // Determine how to trigger change based on staged status:
    switch (status) {
      // Commit the staged value
      case 'commit':
        onChange?.(valid ? staged : previous);
        break;

      /**
       * NOTE: Because of how DayPickerInput updates it's input value, we need
       *       to coerce the previous value to be referentially different to
       *       ensure that the input is updated. Otherwise it will hold onto
       *       the invalid value in the input despite there being a last known
       *       valid value. Additionally, this means the input will only error
       *       on invalid dates if the previous value was empty.
       */
      // Revert to the last known valid date
      case 'revert':
        onChange?.(previous);
        break;

      default:
        break;
    }

    setInvalid(!previous && !valid);
    setStaged({});
    onDayPickerHide?.();
  };

  // Blur input to close calendar on Enter and ESC and passthrough handler
  const handleInputKeyDown = (event, ...rest) => {
    const $input = $picker.current.input;

    /**
     * NOTE: While DayPickerInput has a `hideDayPicker` static method that can
     *       be used to close the calendar. it seems that `keepFocus = true`
     *       will cause the calendar to re-open as focus is reclaimed by the
     *       input's focus trap. Because of this, `blur` is used here to
     *       explicitly prevent the focus trap from re-triggering which in
     *       turn will also close the calendar.
     */
    switch (event.key) {
      case 'Enter':
        $input.blur();
        break;

      case 'Escape':
        setStaged({ status: status && 'revert' });
        $input.blur();
        break;

      default:
        break;
    }

    inputProps.onKeyDown?.(event, ...rest);
  };

  // Render function for overlay component
  const overlay = args => <Overlay footer={footer} portal={portal} {...args} />;
  const caption = args => (
    <Caption onChange={handleDayChange} portal={portal} {...args} />
  );

  return (
    <Wrapper className={className}>
      <DayPickerInput
        // XXX: This is a hack to force DayPickerInput to re-render with initial
        //      state. Since the component holds onto the typed value in its
        //      internal state for invalid values, falsy values provided as
        //      props will cause the input to fallback to the last typed value.
        //      By having the component key prop based on the value provided,
        //      React will force an unmount/mount which will effectively reset
        //      the internal state. Understandably, this solution is not ideal
        //      but does allow us to achieve the result we want without having
        //      to patch the library source code.
        key={value}
        ref={$picker}
        component={Input}
        dayPickerProps={{
          ...dayPickerProps,
          captionElement: caption,
          classNames: classes,
          disabledDays: [
            // NOTE: The ternary below can result in an empty object as one of
            //       the disabled days. In practice this should have no effect,
            //       but we may want to consider spreading an empty array if we
            //       want to prevent that from happening.
            future ? {} : { after: endOfToday },
            ...(dayPickerProps.disabledDays || []),
          ],
          initialMonth: value,
          month: staged || value,
          onDayClick: handleDayClick,
          showOutsideDays: true,
          weekdaysShort: ['S', 'M', 'T', 'W', 'T', 'F', 'S'],
        }}
        format={DATE_FORMAT}
        formatDate={formatDate}
        inputProps={{
          'aria-invalid': error || invalid,
          'aria-label': 'Date picker',
          type: 'text',
          error: error || invalid,
          onKeyDown: handleInputKeyDown,
          ...inputProps,
        }}
        keepFocus={false}
        onDayChange={handleDayChange}
        onDayPickerHide={handleDayPickerHide}
        overlayComponent={overlay}
        parseDate={parseDate}
        placeholder="MM/DD/YY"
        value={value}
        {...props}
      />
    </Wrapper>
  );
}

// Implicitly inherits `react-day-picker/DayPickerInputs` props
DateInput.propTypes = {
  /* eslint-disable react/forbid-prop-types */
  dayPickerProps: PropTypes.object,
  inputProps: PropTypes.object,
  /* eslint-enable react/forbid-prop-types */

  // Footer element within calendar
  footer: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),

  // Convenience prop to allow future dates
  future: PropTypes.bool,

  // Change handler that triggers on picker close for valid dates
  onChange: PropTypes.func,

  // Enhanced dayPickerProps.onDayClick with input instance
  onDayClick: PropTypes.func,

  className: PropTypes.string,
  onDayChange: PropTypes.func,
  onDayPickerHide: PropTypes.func,
  portal: PropTypes.bool,
  value: PropTypes.instanceOf(Date),
};

export default styled(DateInput)``;
